import { describeWorkflow } from 'src/services/analysisStart'
import { candidateFitsWorkflow, shortDay } from 'src/views/NewAnalysis/model/candidates'
import { ANALYSIS_OPTIONS } from 'src/views/NewAnalysis/model/workflow'
import { kitDisplayName } from './inverse'
import { MIN_USES, RECENT_DAYS, SETUP_WINDOW_DAYS } from './setups'
import { suggestName } from './naming'

// ----------------------------------------------------------------------
// Suggestions for Home: the best setups, each with the waiting samples it fits.
// Nothing here starts anything; a suggestion only opens the New analysis panel.
// ----------------------------------------------------------------------

export const MAX_SUGGESTIONS = 3
export const MAX_SETUP_ONLY = 2

const DAY_MS = 24 * 60 * 60 * 1000
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i

// Only analyses the panel offers (Carrier is behind a feature switch).
const OFFERED = new Set(ANALYSIS_OPTIONS.map((option) => option.value))

export const pluralise = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`
const timesText = (n) => (n === 1 ? 'once' : n === 2 ? 'twice' : `${n} times`)

/**
 * The workflow summary for the card: describeWorkflow, with a kit named by its
 * storage name (a user's own kit is stored as "<uuid>.bed") called "Custom kit".
 */
export const summaryOf = (workflow) => {
  if (!workflow) return ''
  let kitName = workflow.kitName
  if (!kitName && workflow.kitId) kitName = UUID_RE.test(workflow.kitId) ? 'Custom kit' : kitDisplayName(workflow.kitId)
  return describeWorkflow({ ...workflow, kitName })
}

// "Germline · Whole exome · Twist Exome v2" -> "Whole exome · Twist Exome v2": the card
// shows the kind as an icon.
export const shortSummaryOf = (workflow) => {
  const summary = summaryOf(workflow)
  const short = summary.replace(/^(Germline|Somatic)( · |$)/, '')
  return short || summary
}

export const kindOf = (workflow) => (workflow?.analysis === 'somatic' ? 'somatic' : 'germline')

// How many times, and over how many days: the last RECENT_DAYS days when the setup
// ran often enough there, else the whole window.
const usageOf = (setup) =>
  setup.recentCount >= MIN_USES
    ? { times: setup.recentCount, days: RECENT_DAYS }
    : { times: setup.count, days: SETUP_WINDOW_DAYS }

const lastUsedText = (ms, now) => {
  const a = new Date(ms)
  const b = new Date(now)
  if (a.toDateString() === b.toDateString()) return 'today'
  if (new Date(now - DAY_MS).toDateString() === a.toDateString()) return 'yesterday'
  return `on ${shortDay(ms)}`
}

/** "Suggested because you ran this setup 38 times in the last 30 days, usually in batches of 12, most recently on 27 Sep." */
export const reasonText = (setup, now = Date.now()) => {
  const { times, days } = usageOf(setup)
  const batches = setup.typicalBatchSize > 1 ? `, usually in batches of ${setup.typicalBatchSize}` : ''
  return `Suggested because you ran this setup ${timesText(
    times,
  )} in the last ${days} days${batches}, most recently ${lastUsedText(setup.lastUsedAt, now)}.`
}

/** "60 waiting samples · used 38× in 30 days", or for a setup only "Used 38× in 30 days · last on 27 Sep". */
export const usageText = (suggestion, now = Date.now()) => {
  const { times, days } = suggestion.usage
  if (suggestion.sampleCount > 0) {
    return `${pluralise(suggestion.sampleCount, 'waiting sample')} · used ${times}× in ${days} days`
  }
  return `Used ${times}× in ${days} days · last ${lastUsedText(suggestion.lastUsedAt, now)}`
}

const toSuggestion = (setup, samples, now, taken) => {
  const sampleCount = samples.length
  const name = suggestName(setup, now, taken)
  return {
    id: setup.signature,
    // The panel's batch name. One sample starts on its own, without a batch.
    name,
    batchName: sampleCount === 1 ? null : name,
    // What the card shows first: the batch name, or the one sample's name.
    title: sampleCount === 1 ? samples[0].key : name,
    titleIsSample: sampleCount === 1,
    workflow: setup.workflow,
    workflowSummary: summaryOf(setup.workflow),
    shortSummary: shortSummaryOf(setup.workflow),
    kind: kindOf(setup.workflow),
    samples,
    sampleCount,
    timesUsed: setup.count,
    usage: usageOf(setup),
    lastUsedAt: setup.lastUsedAt,
    typicalBatchSize: setup.typicalBatchSize,
    setupOnly: sampleCount === 0,
    reason: reasonText(setup, now),
  }
}

/**
 * At most MAX_SUGGESTIONS suggestions, best setup first:
 * [{ id (the setup's signature), name, batchName, title, titleIsSample, workflow,
 *    workflowSummary, shortSummary, kind, samples, sampleCount, timesUsed, usage,
 *    lastUsedAt, typicalBatchSize, setupOnly, reason }].
 *
 * readySamples are Home's "Ready to analyse" items ({ candidate, verifying, ... },
 * newest upload first). Each sample that is not verifying goes to the best-ranked
 * setup it fits (candidateFitsWorkflow: FASTQ to short-read germline or somatic, ONT
 * BAM to ONT, PacBio BAM to PacBio, VCF to VCF, SNV/Indel only for somatic), and only
 * to that one; `samples` holds their candidates, as the panel's initialSamples takes
 * them. When no waiting sample fits any setup, the MAX_SETUP_ONLY best setups come
 * back without samples.
 */
export function buildSuggestions(setups, readySamples, now = Date.now()) {
  const offered = (Array.isArray(setups) ? setups : []).filter((setup) => OFFERED.has(setup?.workflow?.analysis))
  const assigned = new Map(offered.map((setup) => [setup.signature, []]))
  ;(Array.isArray(readySamples) ? readySamples : []).forEach((item) => {
    if (!item || item.verifying || !item.candidate) return
    const setup = offered.find((s) => candidateFitsWorkflow(item.candidate, s.workflow))
    if (setup) assigned.get(setup.signature).push(item.candidate)
  })
  const withSamples = offered.filter((setup) => assigned.get(setup.signature).length > 0)
  const chosen = withSamples.length ? withSamples.slice(0, MAX_SUGGESTIONS) : offered.slice(0, MAX_SETUP_ONLY)
  // Names on the card differ from each other and from the batches already run.
  const taken = offered.flatMap((setup) => (setup.pastBatchNames || []).map((entry) => entry.name))
  return chosen.map((setup) => {
    const suggestion = toSuggestion(setup, assigned.get(setup.signature), now, taken)
    taken.push(suggestion.name)
    return suggestion
  })
}

/** "2 suggestions", or nothing. */
export const suggestionCountText = (count) => (count > 0 ? pluralise(count, 'suggestion') : '')
